import { Divider, List, ListItem, Typography } from '@mui/material';
import parse, { DOMNode, domToReact, Element } from 'html-react-parser';
import { Link } from 'react-router-dom';

const getParserOptions = () => ({
    replace: (domNode: DOMNode) => {
        const element = domNode as Element;
        const hasChildren = Boolean(element.children);

        if (hasChildren) {
            const nestedChild = domToReact(element.children as DOMNode[], getParserOptions());
            switch (element.name) {
                case 'h1': {
                    return (
                        <Typography
                            variant="h1"
                            className="font-museo blog-text-neutral-900 text-4xl sm:text-6xl mb-3 mt-6"
                        >
                            {nestedChild}
                        </Typography>
                    );
                }
                case 'h2':
                    return (
                        <Typography
                            variant="h2"
                            className="font-museo blog-text-neutral-900 text-3xl sm:text-5xl mb-3 mt-6"
                        >
                            {nestedChild}
                        </Typography>
                    );
                case 'h3': {
                    return (
                        <Typography
                            variant="h3"
                            className="font-museo blog-text-neutral-900 text-2xl sm:text-4xl mb-3 mt-6"
                        >
                            {nestedChild}
                        </Typography>
                    );
                }
                case 'h4':
                    return (
                        <h4 className="font-museo blog-text-neutral-900 text-base text-lg font-bold mb-3 mt-6">
                            {nestedChild}
                        </h4>
                    );
                case 'h5':
                    return (
                        <Typography variant="h5" className="font-museo font-bold blog-text-neutral-900">
                            {nestedChild}
                        </Typography>
                    );
                case 'h6':
                    return (
                        <Typography variant="h6" className="font-museo font-italic blog-text-neutral-900">
                            {nestedChild}
                        </Typography>
                    );
                case 'p':
                    const isCaption = Boolean(element.attribs?.class?.includes('wp-caption-text'));
                    if (isCaption) {
                        return (
                            <Typography
                                variant="body2"
                                className="text-xs font-light !font-museo blog-text-neutral-700 tracking-wide text-center mt-3 mb-4"
                            >
                                {nestedChild}
                            </Typography>
                        );
                    }
                    return (
                        <Typography
                            variant="body1"
                            className="text-base/7 sm:text-base font-light !font-inter blog-text-neutral-700 tracking-wide mt-2 mb-3"
                        >
                            {nestedChild}
                        </Typography>
                    );
                case 'a': {
                    const children = element.children as Element[];
                    const hasImage = children.some((child) => child.name === 'img');

                    return (
                        <Link
                            to={element.attribs?.href}
                            className={hasImage ? 'block w-full mx-auto' : 'text-blog-primary-700'}
                            target={'_blank'}
                        >
                            {nestedChild}
                        </Link>
                    );
                }
                case 'em': {
                    return <em className="font-museo blog-text-neutral-900 font-medium">{nestedChild}</em>;
                }
                case 'ul':
                    return <List className="list-disc marker:text-spanish-blue space-y-2 pl-4">{nestedChild}</List>;
                case 'ol':
                    return <List className="list-decimal marker:text-spanish-blue space-y-2 pl-4">{nestedChild}</List>;
                case 'li':
                    return (
                        <ListItem
                            sx={{
                                display: 'list-item',
                            }}
                            className="text-base/7 sm:text-base font-light font-inter blog-text-neutral-700 tracking-wide pl-2"
                        >
                            {nestedChild}
                        </ListItem>
                    );
                case 'hr':
                    return <Divider />;
                case 'img': {
                    element.attribs.class = 'w-[90%] mx-auto mt-6';
                    return nestedChild;
                }
                case 'blockquote': {
                    return <blockquote className="italic font-semibold p-4 my-4 border-l-4">{nestedChild}</blockquote>;
                }
                case 'div': {
                    return <div className="w-full">{nestedChild}</div>;
                }
                default:
                    return nestedChild;
            }
        }

        return element;
    },
});

export const parseHtml = (htmlString: string) => {
    return parse(htmlString, getParserOptions());
};
